<template>
  <div class="home">
    <MaintenanceBanner />

    <div v-if="portalLoading" id="pre-loader-wrapper">
      <div id="pre-loader">
        <div class="loader"></div>
        <h2>Loading {{ loadingText }}...</h2>
      </div>
    </div>
    <div id="application-version">v1.14.2-1-gc5ce337</div>
    <div class="main">
      <ProgressBar id="ProgressBar" :current-group-name="currentGroupName" />

      <div id="application-texts">
        <div id="nameField">
          <span>Logged in as: </span>
          <span
            >{{ $store.state.user.firstname }}
            {{ $store.state.user.middlename }}
            {{ $store.state.user.lastname }}</span
          >
          <span
            v-if="$store.state.user.lastname && $store.state.user.companyname"
          >
            -
          </span>
          <span v-if="$store.state.user.companyname">{{
            $store.state.user.companyname
          }}</span>
        </div>
        <div
          id="language-field"
          @click="languageChange()"
          style="cursor: pointer;"
        >
          <span style="margin-right: 5px;">{{
            $store.state.localLanguagePack?.displayName ?? "Language not found"
          }}</span>
          <span><i class="fas fa-globe" style="color: #da6f38;"></i></span>
        </div>
      </div>

      <div id="rmaFormViewWrapper" class="padding-form">
        <component
          :is="currentView"
          @change-page="changePage"
          ref="currentViewComponent"
        />
      </div>

      <RmaViewSelector
        @page-next="nextPage"
        @page-prev="previousPage"
        :show-next-button="showNextButton"
        :show-prev-button="showPreviousButton"
      />

      <div id="liveRmaWrapper" class="liverma-padding">
        <LiveRma />
      </div>

      <div id="rmaListWrapper">
        <UserRmaListing />
      </div>
    </div>
    <div v-if="$store.state.isShadow">
      <button @click="logout">Logout</button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";

import DealerSpecialOptions from "@/components/RmaPages/DealerOptions.vue";
import ModelSelection from "@/components/RmaPages/ModelSelection.vue";
import PackageInformation from "@/components/RmaPages/PackageInformation.vue";
import RmaConfirmation from "@/components/RmaPages/RmaConfirmation.vue";
import RmaReason from "@/components/RmaPages/RmaReason.vue";
import RmaReferences from "@/components/RmaPages/RmaReferences.vue";
import TransportSelection from "@/components/RmaPages/TransportSelection.vue";
import TransportServicepoints from "@/components/RmaPages/TransportServicepoints.vue";
import WarrantyInformation from "@/components/RmaPages/WarrantyInformation.vue";
import MaintenanceBanner from "@/components/MaintenanceBanner.vue";

import LiveRma from "@/components/LiveRma.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import RmaViewSelector from "@/components/RmaViewSelector.vue";
import UserRmaListing from "@/components/UserRmaListing.vue";

import { getLanguageFields } from "@/multilanguage";
import { AxiosError } from "axios";
import { VueConstructor } from "vue";

interface Page {
  name: PageName;
  component: VueConstructor;
  groupName: string;
}

export interface PageChangeContext {
  newPage: PageName;
  oldPage: PageName;
  hasVisitedNewPage: boolean;
  isReturnOperation: boolean; // Indicates if the page change is a return to a previous page, we choose to perform less validation in this case
}

export type PageName = string;

export interface PageComponent {
  beforePageLeave: (arg0: PageChangeContext) => Promise<boolean>;
  onPageLeave: (arg0: PageChangeContext) => Promise<PageName>;
  afterPageEnter: (arg0: PageChangeContext) => Promise<void>;
}

import * as Sentry from "@sentry/vue";

@Component({
  components: {
    ModelSelection,
    WarrantyInformation,
    RmaReason,
    TransportSelection,
    TransportServicepoints,
    PackageInformation,
    LiveRma,
    RmaViewSelector,
    ProgressBar,
    UserRmaListing,
    RmaReferences,
    RmaConfirmation,
    DealerSpecialOptions,
    MaintenanceBanner
  }
})
export default class CreateRma extends Vue {
  public language = 1;
  public loadingText = "Requests";
  public portalLoading = true;

  public pages: Page[] = [
    {
      name: "ModelSelection",
      groupName: "ProductDetails",
      component: ModelSelection
    },
    {
      name: "DealerSpecialOptions",
      groupName: "DealerOptions",
      component: DealerSpecialOptions
    },
    {
      name: "WarrantyInformation",
      groupName: "WarrantyInformation",
      component: WarrantyInformation
    },
    {
      name: "RmaReason",
      groupName: "ProblemDescription",
      component: RmaReason
    },
    {
      name: "TransportSelection",
      groupName: "TransportOptions",
      component: TransportSelection
    },
    {
      name: "TransportServicepoints",
      groupName: "TransportOptions",
      component: TransportServicepoints
    },
    {
      name: "PackageInformation",
      groupName: "TransportOptions",
      component: PackageInformation
    },
    {
      name: "RmaReferences",
      groupName: "ConfirmationOptions",
      component: RmaReferences
    },
    {
      name: "RmaConfirmation",
      groupName: "ConfirmationOptions",
      component: RmaConfirmation
    }
  ];

  public pageHistory: number[] = [];
  public currentPageIndex = 0;

  public get currentPageName(): string {
    return this.pages[this.currentPageIndex].name;
  }

  public get currentView(): VueConstructor {
    return this.pages[this.currentPageIndex].component;
  }

  public get currentGroupName(): string {
    return this.pages[this.currentPageIndex].groupName;
  }

  public get showNextButton(): boolean {
    return this.currentPageName !== "RmaConfirmation";
  }

  public get showPreviousButton(): boolean {
    return this.currentPageName !== "ModelSelection";
  }

  async mounted(): Promise<void> {
    Vue.prototype.$changePage = this.changePage.bind(this);

    this.loadingText = "authentication";

    // Refreshing token with Joomla token
    if (this.$store.state?.isShadow !== true)
      await this.joomlaAuthentication().catch(() => {
        console.info("Joomla token wasn't available.");
      });

    try {
      this.loadingText = "language fields";
      const languageResponse = await getLanguageFields(this);
      this.$store.state.localLanguagePack = languageResponse;
      this.loadingText = "saved RMA's";
      await this.$store.dispatch("downloadUserRmas");

      this.loadingText = "RMA helper data";
      await this.$store.getters.getRmaReasons;
      this.loadingText = "User information";
      await this.$store.dispatch("getUserInfo");
      this.portalLoading = false;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status == 401 || error.response?.status == 403) {
          console.info(
            "Required requests were unsuccessful, redirecting to login."
          );
          if (this.$router.currentRoute.path !== "/login")
            this.$router.push("/login");
        }
      } else {
        console.error(error);
      }
    }
  }

  /**
   * Switch the current RMA page. Old page's beforePageLeave() is called before the new page is loaded.
   *
   * This function replaces the old page logic system that used a manual index to keep track of the current page.
   * @param page
   * @param ctx
   * @returns If the page change was successful
   */
  public async changePage(
    page: string,
    ctx = { saveRma: true, skipPageExit: false, clearHistory: false }
  ): Promise<boolean> {
    ctx.saveRma = ctx.saveRma ?? true;
    ctx.skipPageExit = ctx.skipPageExit ?? false;
    ctx.clearHistory = ctx.clearHistory ?? false;

    Sentry.metrics.count(`page_change_${page}`, 1);

    const newPageIndex = this.pages.findIndex(p => p.name === page);

    if (newPageIndex === -1) {
      console.error(`Page ${page} not found.`);
      return false;
    }

    // Create context object for the beforePageLeave() and afterPageEnter() functions
    const context: PageChangeContext = {
      hasVisitedNewPage: this.pageHistory.includes(newPageIndex),
      newPage: page,
      oldPage: this.pages[this.currentPageIndex].name,
      isReturnOperation: false
    };

    // Call beforePageLeave() on the old page.
    const canChange = ctx.skipPageExit
      ? true
      : await (this.$refs.currentViewComponent as Vue &
          PageComponent).beforePageLeave(context);

    if (!canChange) {
      return false;
    }

    // Add the old page to the history
    this.pageHistory.push(this.currentPageIndex);

    if (ctx.clearHistory) {
      this.pageHistory = [];
      context.hasVisitedNewPage = false;
    }

    // Only save the RMA if it hasn't been saved yet
    if (ctx.saveRma) {
      await this.$store.dispatch("uploadRma").catch(async error => {
        const eventId = Sentry.captureException(error);

        if (error instanceof AxiosError) {
          await this.$swal({
            title: "Unexpected RMA state",
            text: `The current RMA could not be saved, please try to create a new RMA or contact the support team. (Support reference: w-${eventId})`,
            icon: "error",
            footer: error.response?.data.error
          });
        } else {
          await this.$swal({
            title: "Unexpected RMA state",
            text:
              "The current RMA could not be saved, please try to create a new RMA or contact the support team.",
            icon: "error"
          });
        }
      });
    }

    // Change the current page
    this.currentPageIndex = newPageIndex;

    // Call afterPageEnter() on the new page
    await (this.$refs.currentViewComponent as Vue &
      PageComponent).afterPageEnter(context);

    // Build a new history using the onPageLeave() functions recursively from the first page to the new page.
    if (ctx.clearHistory) {
      let maxLoop = 0;
      let historyBuilderIndex = this.pages.findIndex(
        p => p.name === "ModelSelection"
      );

      while (historyBuilderIndex !== this.currentPageIndex) {
        if (maxLoop++ > 100) {
          console.error("Infinite loop detected while building history.");
          this.pageHistory = [];
          this.currentPageIndex = 0;
          return false;
        }

        const pageComponent = new this.pages[historyBuilderIndex].component({
          mounted: () => null,
          parent: this
        });

        const nextPage = await (pageComponent as Vue &
          PageComponent).onPageLeave(context);
        this.pageHistory.push(historyBuilderIndex);
        historyBuilderIndex = this.pages.findIndex(p => p.name === nextPage);
      }
    }
    return true;
  }

  public async nextPage(): Promise<void> {
    const nextPage = await (this.$refs.currentViewComponent as Vue &
      PageComponent).onPageLeave({
      hasVisitedNewPage: false,
      newPage: this.pages[this.currentPageIndex + 1]?.name ?? "",
      oldPage: this.pages[this.currentPageIndex].name,
      isReturnOperation: false
    });

    if (nextPage) await this.changePage(nextPage);
  }

  public async previousPage(): Promise<void> {
    const currentPageIndex = this.currentPageIndex;
    const previousPageIndex = this.pageHistory.pop();

    const context: PageChangeContext = {
      hasVisitedNewPage: true,
      newPage: this.pages[previousPageIndex].name,
      oldPage: this.pages[this.currentPageIndex].name,
      isReturnOperation: true
    };

    if (previousPageIndex !== undefined) {
      const canExit = await (this.$refs.currentViewComponent as Vue &
        PageComponent).beforePageLeave(context);

      if (!canExit) {
        // Re-add the page to the history if we can't exit
        this.pageHistory.push(previousPageIndex);
        return;
      }

      this.currentPageIndex = previousPageIndex;
      await (this.$refs.currentViewComponent as Vue &
        PageComponent).afterPageEnter({
        hasVisitedNewPage: this.pageHistory.includes(this.currentPageIndex),
        newPage: this.pages[this.currentPageIndex].name,
        oldPage: this.pages[currentPageIndex].name,
        isReturnOperation: true
      });
    }
  }

  /**
   * Authenticate with Joomla token
   *
   * @returns {Promise<void>}
   * @throws {Error} Axios error
   */
  async joomlaAuthentication(): Promise<void> {
    // Try to authenticate with Joomla token
    const response = await this.$axios.post("/login/joomla");
    if (response && response.data) {
      this.$store.commit("setToken", response.data.token);
      this.$axios.defaults.headers.common.Authorization =
        "Bearer " + localStorage.getItem("authenticationToken");
      return;
    }
  }

  async languageChange() {
    const languageRequest = await this.$axios.get("/language");

    Sentry.metrics.count("language_change", 1);

    const languages = languageRequest.data.languages.map(language => {
      return {
        id: language.id,
        name: language.displayName
      };
    });

    const inputOptions = languages.reduce((acc, language) => {
      acc[language.id] = language.name;
      return acc;
    }, {});

    await this.$swal.fire({
      title: this.$mlGet("language-change-title"),
      input: "select",
      inputOptions,
      inputPlaceholder: this.$mlGet("language-change-placeholder"),
      preConfirm: (language: string) => {
        return this.$axios
          .put("/user/language", { language })
          .then(async response => {
            if (response.status === 200) {
              const languageResponse = await getLanguageFields(this, +language);
              this.$store.state.localLanguagePack = languageResponse;
            }
          })
          .catch(error => {
            this.$swal.showValidationMessage(
              `Request failed: ${error?.response?.data?.error ?? error}`
            );
          });
      }
    });
  }

  logout(): void {
    this.$store.commit("setToken", "");
    this.$router.push("/login");
  }
}
</script>

<style>
.information-box {
  color: #da6f38;
  font-style: italic;
  font-size: 100%;
}

.loader {
  border: 16px solid #f3f3f3;
  border-top: 16px solid #da6f38;
  border-radius: 50%;
  width: 120px;
  height: 120px;
  animation: spin 2s linear infinite;
}

.loader-positioning {
  position: absolute;
  top: calc(30vh - 60px);
  left: calc(50vw - 60px);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>

<style scoped>
.main {
  background-color: white;
  display: grid;
  grid-template-columns: 40vw auto;
  grid-template-rows: auto 2ex 40ex auto auto auto auto;
}

.liverma-padding {
  padding: 1ex;
  padding-left: 0;
}

.padding-form {
  padding: 1ex;
}

#rmaFormViewWrapper {
  grid-column: 2 / 3;
  grid-row: 3 / 5;
}

#liveRmaWrapper {
  grid-column: 1 / 2;
  grid-row: 3 / 6;
  overflow: hidden;
}

#rmaListWrapper {
  grid-column: 1 / 3;
  grid-row: 7 / 8;
}

.RmaViewSelector {
  grid-column: 2 / 3;
  grid-row: 5 / 7;
}

.formSelector button {
  background-color: #da6f38;
  border: none;
  padding: 4px 20px;
  border-radius: 3px;
  margin: 0px 10px;
  color: white;
  font-weight: bold;
  width: auto;
  cursor: pointer;
}

#progressBar {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
}

#progressBar tr {
  padding: 10px;
  margin: 0;
}

#ProgressBar {
  grid-column: 1 / 3;
  grid-row: 1 / 2;
}

#pre-loader-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;
}

#pre-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

#application-version {
  position: absolute;
  font-size: 80%;
  top: 1px;
  right: 1px;
  color: gray;
}

#application-texts {
  grid-column: 1 / 3;
  display: flex;
  margin: 5px 10px 10px 5px;
  font-size: 80%;
  justify-content: space-between;
}
</style>
